import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"

export default class extends Controller {
  static targets = ["paymentForm", "selectedPlan", "cardElement", "cardErrors", "submitButton",
                     "promoCodeInput", "promoCodeSuccess", "promoCodeError", "originalPrice",
                     "finalPrice", "discountAmount", "discountDisplay", "priceDisplay"]
  static values = { publishableKey: String }

  async connect() {
    console.log('Plan subscription controller connected')
    console.log('Publishable key:', this.publishableKeyValue)

    this.stripe = await loadStripe(this.publishableKeyValue)
    this.elements = this.stripe.elements()
    this.cardElement = this.elements.create('card', {
      hidePostalCode: true
    })
    this.cardElement.mount(this.cardElementTarget)

    this.cardElement.on('change', (event) => {
      if (event.error) {
        this.cardErrorsTarget.textContent = event.error.message
      } else {
        this.cardErrorsTarget.textContent = ''
      }
    })
  }

  selectPlan(event) {
    console.log('selectPlan called')
    const planId = event.currentTarget.dataset.planId
    const planPrice = event.currentTarget.dataset.planPrice
    const planName = event.currentTarget.dataset.planName

    console.log('Plan data:', { planId, planPrice, planName })

    this.selectedPlanId = planId
    this.selectedPlanPrice = parseInt(planPrice)
    this.selectedPlanName = planName
    this.appliedPromoCode = null
    this.discountAmount = 0

    this.selectedPlanTarget.textContent = `${planName} - ¥${this.selectedPlanPrice.toLocaleString()}/月`
    this.priceDisplayTarget.style.display = 'none'
    this.promoCodeInputTarget.value = ''
    this.promoCodeSuccessTarget.style.display = 'none'
    this.promoCodeErrorTarget.style.display = 'none'

    this.paymentFormTarget.style.display = 'block'
    this.paymentFormTarget.scrollIntoView({ behavior: 'smooth' })
  }

  async submit(event) {
    if (!this.selectedPlanId) {
      this.showError('プランを選択してください')
      return
    }

    // ローディング画面を表示
    this.showLoadingOverlay()

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>処理中...'

    try {
      // Step 1: SetupIntentを作成
      const response = await fetch(`/payments/subscribe/${this.selectedPlanId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'プラン登録の準備に失敗しました')
      }

      // Step 2: SetupIntentで支払い方法を確認
      const { error, setupIntent } = await this.stripe.confirmCardSetup(data.client_secret, {
        payment_method: {
          card: this.cardElement,
          billing_details: {
            name: data.customer_name
          }
        }
      })

      if (error) {
        this.showError(this.getJapaneseErrorMessage(error))
      } else {
        // Step 3: サブスクリプションを作成
        await this.confirmSubscription(setupIntent.id)
      }
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.hideLoadingOverlay()
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i>プランに登録'
    }
  }

  async confirmSubscription(setupIntentId) {
    try {
      console.log('Confirming subscription with setup_intent_id:', setupIntentId)

      const response = await fetch(`/payments/confirm_plan_subscription/${this.selectedPlanId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          setup_intent_id: setupIntentId,
          promo_code: this.appliedPromoCode
        })
      })

      const data = await response.json()
      console.log('Server response:', response.status, data)

      if (response.ok) {
        // 基本の成功メッセージ
        let successMessage = 'プランに登録しました！'

        // 仮予約確定結果がある場合は詳細メッセージを追加
        if (data.tentative_result) {
          const confirmed = data.tentative_result.confirmed || 0
          const canceled = data.tentative_result.canceled || 0

          if (confirmed > 0 || canceled > 0) {
            successMessage += '\n\n【仮予約の確定結果】\n'
            if (confirmed > 0) {
              successMessage += `✅ ${confirmed}件の仮予約が確定されました\n`
            }
            if (canceled > 0) {
              successMessage += `❌ ${canceled}件の仮予約がセッション不足でキャンセルされました`
            }
          }
        }

        // 複数行メッセージ対応のため、モーダルで表示
        this.showSuccessModal(successMessage)

        // 3秒後にリロード
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        console.error('Server error response:', data)
        this.showError(data.error || 'プラン登録の確認に失敗しました')
      }
    } catch (error) {
      console.error('Error in confirmSubscription:', error)
      this.showError('プラン登録の確認に失敗しました')
    }
  }

  getJapaneseErrorMessage(error) {
    const errorMessages = {
      'card_declined': 'カードが拒否されました。別のカードをお試しください。',
      'expired_card': 'カードの有効期限が切れています。',
      'incorrect_cvc': 'セキュリティコードが正しくありません。',
      'processing_error': '決済処理中にエラーが発生しました。',
      'incorrect_number': 'カード番号が正しくありません。',
      'invalid_expiry_month': '有効期限の月が正しくありません。',
      'invalid_expiry_year': '有効期限の年が正しくありません。',
      'invalid_cvc': 'セキュリティコードが正しくありません。',
      'insufficient_funds': '残高不足です。',
      'generic_decline': 'カードが拒否されました。カード会社にお問い合わせください。'
    }

    return errorMessages[error.code] || error.message || '決済処理中にエラーが発生しました。'
  }

  showError(message) {
    this.showToast(message, 'error')
  }

  showSuccess(message) {
    this.showToast(message, 'success')
  }

  showSuccessModal(message) {
    // 既存のモーダルがあれば削除
    const existingModal = document.getElementById('tentativeResultModal')
    if (existingModal) {
      existingModal.remove()
    }

    // モーダルHTML作成
    const modalHtml = `
      <div class="modal fade" id="tentativeResultModal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-success text-white">
              <h5 class="modal-title">
                <i class="bi bi-check-circle me-2"></i>
                決済完了
              </h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <div class="text-center py-3">
                <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
                <div class="mt-3" style="white-space: pre-line;">${message}</div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-primary" data-bs-dismiss="modal">確認</button>
            </div>
          </div>
        </div>
      </div>
    `

    // モーダルをbodyに追加
    document.body.insertAdjacentHTML('beforeend', modalHtml)

    // Bootstrapモーダルを表示
    const modalElement = document.getElementById('tentativeResultModal')
    import('bootstrap').then(({ Modal }) => {
      const modal = new Modal(modalElement)
      modal.show()

      // モーダルが閉じられたら要素を削除
      modalElement.addEventListener('hidden.bs.modal', () => {
        modalElement.remove()
      })
    }).catch(() => {
      // フォールバック: bootstrapがロードできない場合は通常のアラート
      alert(message)
    })
  }

  async cancelPlan(event) {
    if (!confirm('プランを解約しますか？現在の期間終了まで利用可能です。')) {
      return
    }

    const button = event.currentTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>処理中...'

    try {
      const response = await fetch('/payments/cancel_plan', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showSuccess(`プランを解約しました。${data.period_end}まで利用可能です。`)
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        this.showError(data.error || 'プラン解約に失敗しました')
      }
    } catch (error) {
      this.showError('プラン解約に失敗しました')
    } finally {
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  showToast(message, type) {
    const toast = document.createElement('div')
    toast.className = `toast align-items-center text-white bg-${type === 'error' ? 'danger' : 'success'} border-0`
    toast.setAttribute('role', 'alert')
    toast.innerHTML = `
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
      </div>
    `

    const container = document.querySelector('.toast-container') || this.createToastContainer()
    container.appendChild(toast)

    // window.bootstrapからToastを取得
    const Toast = window.bootstrap?.Toast
    if (Toast) {
      const bsToast = new Toast(toast)
      bsToast.show()
    } else {
      // フォールバック: 動的インポート
      import('bootstrap').then(bootstrap => {
        const bsToast = new bootstrap.Toast(toast)
        bsToast.show()
      }).catch(() => {
        console.error('Bootstrap Toast could not be loaded')
        // フォールバック: 単純な表示
        toast.style.display = 'block'
        setTimeout(() => {
          toast.style.display = 'none'
          toast.remove()
        }, 5000)
      })
    }

    toast.addEventListener('hidden.bs.toast', () => {
      toast.remove()
    })
  }

  createToastContainer() {
    const container = document.createElement('div')
    container.className = 'toast-container position-fixed top-0 end-0 p-3'
    container.style.zIndex = '1055'
    document.body.appendChild(container)
    return container
  }

  async applyPromoCode() {
    const promoCode = this.promoCodeInputTarget.value.trim()

    if (!promoCode) {
      this.promoCodeErrorTarget.textContent = 'プロモーションコードを入力してください'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
      return
    }

    if (!this.selectedPlanId) {
      this.promoCodeErrorTarget.textContent = 'プランを選択してください'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
      return
    }

    try {
      const response = await fetch('/payments/validate_promo_code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          code: promoCode,
          plan_id: this.selectedPlanId,
          amount: this.selectedPlanPrice
        })
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        this.appliedPromoCode = promoCode
        this.discountAmount = data.discount_amount
        const finalPrice = this.selectedPlanPrice - this.discountAmount

        this.promoCodeSuccessTarget.textContent = `${data.display_discount} 適用されました`
        this.promoCodeSuccessTarget.style.display = 'block'
        this.promoCodeErrorTarget.style.display = 'none'

        this.originalPriceTarget.textContent = `¥${this.selectedPlanPrice.toLocaleString()}`
        this.discountAmountTarget.textContent = `-¥${this.discountAmount.toLocaleString()}`
        this.finalPriceTarget.textContent = `¥${finalPrice.toLocaleString()}`
        this.priceDisplayTarget.style.display = 'block'
      } else {
        this.appliedPromoCode = null
        this.discountAmount = 0
        this.promoCodeErrorTarget.textContent = data.error || '無効なプロモーションコードです'
        this.promoCodeErrorTarget.style.display = 'block'
        this.promoCodeSuccessTarget.style.display = 'none'

        this.priceDisplayTarget.style.display = 'none'
      }
    } catch (error) {
      this.promoCodeErrorTarget.textContent = 'エラーが発生しました'
      this.promoCodeErrorTarget.style.display = 'block'
      this.promoCodeSuccessTarget.style.display = 'none'
    }
  }

  checkPromoCode() {
    // Clear messages when user starts typing
    if (this.promoCodeInputTarget.value.trim() === '') {
      this.promoCodeSuccessTarget.style.display = 'none'
      this.promoCodeErrorTarget.style.display = 'none'
    }
  }

  showLoadingOverlay() {
    // 既存のオーバーレイがあれば削除
    const existingOverlay = document.getElementById('paymentLoadingOverlay')
    if (existingOverlay) {
      existingOverlay.remove()
    }

    // ローディングオーバーレイを作成
    const overlay = document.createElement('div')
    overlay.id = 'paymentLoadingOverlay'
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 9999;
    `

    overlay.innerHTML = `
      <div class="spinner-border text-light" style="width: 3rem; height: 3rem;" role="status">
        <span class="visually-hidden">処理中...</span>
      </div>
      <div class="text-white mt-3">
        <h5>決済処理中...</h5>
        <p class="mb-0">しばらくお待ちください</p>
        <p class="small mt-2">画面を閉じないでください</p>
      </div>
    `

    document.body.appendChild(overlay)
  }

  hideLoadingOverlay() {
    const overlay = document.getElementById('paymentLoadingOverlay')
    if (overlay) {
      overlay.remove()
    }
  }
}
